import { skipToken } from '@reduxjs/toolkit/query';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import Button from 'components/actions/Button';
import IconButton from 'components/actions/IconButton';
import Can from 'components/auth/Can';
import NoData from 'components/content/NoData';
import Tooltip from 'components/overlays/Tooltip';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useListSystemFleetsQuery } from 'services/systemAdmin/fleets';
import { useListUsersQuery } from 'services/user';
import {
    useDeleteVehicleMutation,
    useGetActiveMissionStateQuery,
    useGetVehicleByVinQuery,
    useSummaryVehiclesV2Query,
} from 'services/vehiclesV2';
import {
    ActiveSidebar,
    clearActiveElements,
    getActiveSidebar,
    getSelectedVehicleVin,
    setVehicleDialog,
} from 'store/slices/settingsPageSlice';
import { getDatePattern, getTimePattern } from 'store/slices/userSettingsSlice';

import type { SectionItem } from '../SidebarSection';
import DeleteVehicleDialog from './DeleteVehicleDialog';
import {
    type DeleteVehicleBlockReason,
    type DeleteVehicleEligibility,
    getDeleteVehicleEligibility,
} from './deleteVehicleEligibility';
import SystemVehiclesSidebarContent from './SystemVehiclesSidebarContent';
import { formatDateTimeWithDash, getVehicleTypeName, isVehicleStale } from './systemVehicleUtils';

import './styles/SystemVehiclesSidebar.scss';

const DELETE_TOOLTIP_TEXT: Record<DeleteVehicleBlockReason, string> = {
    AUTONOMOUS: "Vehicle can't be deleted due to currently being in autonomous mode",
    ACTIVE_MISSION: "Vehicle can't be deleted due to currently being assigned to or scheduled for a mission",
    LOADING: 'Checking whether the vehicle can be deleted',
    VALIDATION_ERROR: "Vehicle can't be deleted because its current state could not be verified",
};

interface FooterProps {
    openVehicleDialog(): void;
    openDeleteVehicleDialog(): void;
    deleteEligibility: DeleteVehicleEligibility;
}

const Footer = ({ openVehicleDialog, openDeleteVehicleDialog, deleteEligibility }: FooterProps) => {
    const deleteTooltip = deleteEligibility.allowed ? 'Delete' : DELETE_TOOLTIP_TEXT[deleteEligibility.reason];

    return (
        <div className='SystemVehiclesSidebar__footer'>
            <Can type='fleets' action='edit'>
                <Button
                    variant='outline'
                    style='primary'
                    className='SystemVehiclesSidebar__editButton'
                    onClick={openVehicleDialog}
                >
                    <span className='rioglyph rioglyph-pencil mr-5' />
                    Edit
                </Button>
            </Can>
            <Can type='fleets' action='edit'>
                <Tooltip
                    placement='top-end'
                    variant='plain'
                    classNames='SystemVehiclesSidebar__deleteTooltipTrigger'
                    data-testid='system-vehicles-sidebar-delete-tooltip'
                    trigger={
                        <IconButton
                            aria-label='Delete vehicle'
                            variant='outline'
                            style='danger'
                            className='SystemVehiclesSidebar__deleteButton'
                            onClick={openDeleteVehicleDialog}
                            disabled={!deleteEligibility.allowed}
                            data-testid='system-vehicles-sidebar-delete-vehicle'
                        >
                            <span className='rioglyph rioglyph-trash' />
                        </IconButton>
                    }
                >
                    {deleteTooltip}
                </Tooltip>
            </Can>
        </div>
    );
};

const SystemVehiclesSidebar = () => {
    const dispatch = useDispatch();
    const [deleteVehicleVin, setDeleteVehicleVin] = useState<string | null>(null);
    const activeSidebar = useSelector(getActiveSidebar);
    const selectedVehicleVin = useSelector(getSelectedVehicleVin);
    const datePattern = useSelector(getDatePattern);
    const timePattern = useSelector(getTimePattern);

    const { data: fleets = [] } = useListSystemFleetsQuery('ListFleets');
    const { data: vehicles = [] } = useSummaryVehiclesV2Query();
    const { data: users = [] } = useListUsersQuery('ListUsers');
    const [deleteVehicle, { isLoading: isDeleting, isError: isDeleteError, error: deleteError, reset: resetDelete }] =
        useDeleteVehicleMutation();
    const deleteErrorStatus =
        typeof deleteError === 'object' &&
        deleteError !== null &&
        'status' in deleteError &&
        typeof deleteError.status === 'number'
            ? deleteError.status
            : undefined;

    const selectedVehicle = vehicles?.find(v => v.vin === selectedVehicleVin);
    const physicalVehicleVin = selectedVehicle?.subtype === 'PHYSICAL_VEHICLE' ? selectedVehicle.vin : undefined;
    const {
        data: vehicle,
        isLoading: isVehicleLoading,
        isFetching: isVehicleFetching,
        isError: isVehicleError,
    } = useGetVehicleByVinQuery(physicalVehicleVin ?? skipToken);
    const {
        data: missionState,
        isLoading: isMissionLoading,
        isFetching: isMissionFetching,
        isError: isMissionError,
    } = useGetActiveMissionStateQuery(physicalVehicleVin ?? skipToken, {
        refetchOnMountOrArgChange: true,
    });
    const selectedFleet = fleets.find(fleet => fleet.id === selectedVehicle?.fleetId);
    const creator = (users ?? []).find(u => u.id === selectedVehicle?.createdBy);

    const deleteEligibility: DeleteVehicleEligibility = selectedVehicle
        ? getDeleteVehicleEligibility({
              subtype: selectedVehicle.subtype,
              adsState: vehicle?.ads.value?.value,
              missionState,
              isVehicleLoading: isVehicleLoading || isVehicleFetching,
              isVehicleError,
              isMissionLoading: isMissionLoading || isMissionFetching,
              isMissionError,
          })
        : { allowed: false, reason: 'LOADING' };

    const closeSidebar = () => {
        dispatch(clearActiveElements());
    };

    const vehicleDetailsSectionItems: SectionItem[] = [
        {
            label: 'License Plate',
            element: selectedVehicle?.licensePlate ?? <NoData text='No data' className='text-size-14' />,
        },
        {
            label: 'VIN',
            element: selectedVehicle?.vin ?? <NoData text='No data' className='text-size-14' />,
        },
        {
            label: 'Type',
            element: selectedVehicle ? (
                getVehicleTypeName(selectedVehicle)
            ) : (
                <NoData text='No data' className='text-size-14' />
            ),
        },
        {
            label: 'Assigned Fleet',
            element: selectedFleet?.name ?? <NoData text='No data' className='text-size-14' />,
        },
    ];

    const createdBySectionItems: SectionItem[] = [
        {
            label: 'Created',
            element: selectedVehicle?.createdAt ? (
                formatDateTimeWithDash(selectedVehicle?.createdAt, datePattern, timePattern)
            ) : (
                <NoData text='No data' className='text-size-14' />
            ),
        },
        {
            label: 'Created by',
            element: [creator?.firstName, creator?.lastName].filter(Boolean).join(' ') || (
                <NoData text='No data' className='text-size-14' />
            ),
        },
    ];

    const openVehicleDialog = () => {
        if (selectedVehicle) {
            dispatch(setVehicleDialog({ status: 'open', mode: 'update', vehicle: selectedVehicle }));
        }
    };

    const openDeleteVehicleDialog = () => {
        if (selectedVehicle && deleteEligibility.allowed) {
            resetDelete();
            setDeleteVehicleVin(selectedVehicle.vin);
        }
    };

    const closeDeleteVehicleDialog = () => {
        resetDelete();
        setDeleteVehicleVin(null);
    };

    const confirmDeleteVehicle = async () => {
        if (!deleteVehicleVin) {
            return;
        }

        try {
            await deleteVehicle(deleteVehicleVin).unwrap();
            setDeleteVehicleVin(null);
            closeSidebar();
        } catch {
            // The mutation state is rendered in the dialog so the user can retry.
        }
    };

    return (
        <>
            <Sidebar
                className='width-400'
                title={<p className='text-bold text-size-16 line-height-16 text-color-darkest'>Vehicle information</p>}
                titleClassName='pl-10'
                footer={
                    <Footer
                        openVehicleDialog={openVehicleDialog}
                        openDeleteVehicleDialog={openDeleteVehicleDialog}
                        deleteEligibility={deleteEligibility}
                    />
                }
                closed={activeSidebar !== ActiveSidebar.VEHICLES}
                onClose={closeSidebar}
                fly
            >
                <SystemVehiclesSidebarContent
                    vehicleName={selectedVehicle?.name ?? ''}
                    isStale={selectedVehicle ? (isVehicleStale(selectedVehicle) ?? false) : false}
                    vehicleDetailsSectionItems={vehicleDetailsSectionItems}
                    createdBySectionItems={createdBySectionItems}
                />
            </Sidebar>
            <DeleteVehicleDialog
                show={deleteVehicleVin !== null}
                isLoading={isDeleting}
                isError={isDeleteError}
                errorStatus={deleteErrorStatus}
                onCancel={closeDeleteVehicleDialog}
                onConfirm={confirmDeleteVehicle}
            />
        </>
    );
};

export default SystemVehiclesSidebar;
